import { motion } from 'framer-motion';
import { 
  Download, 
  ExternalLink, 
  MonitorPlay, 
  Search, 
  Server, 
  Gamepad2,
  CheckCircle2,
  MessageCircle
} from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const steps = [
  {
    number: 1,
    title: 'Installer FiveM',
    description: 'Téléchargez et installez le client FiveM depuis le site officiel.',
    icon: Download,
    action: {
      label: 'Télécharger FiveM',
      url: 'https://fivem.net/',
      external: true
    }
  },
  {
    number: 2,
    title: 'Lancer FiveM',
    description: 'Ouvrez FiveM et connectez votre compte Cfx.re (ou créez-en un).',
    icon: MonitorPlay
  },
  {
    number: 3,
    title: 'Rechercher le serveur',
    description: 'Dans la barre de recherche, tapez "mcommunity" ou utilisez notre lien de connexion directe.',
    icon: Search
  },
  {
    number: 4,
    title: 'Rejoindre',
    description: 'Cliquez sur le serveur pour le rejoindre. Première connexion ? Lisez le règlement !',
    icon: Server
  }
];

const Join = () => {
  const { settings } = useSettings();

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Header */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-violet-600/20 mb-6">
              <Gamepad2 className="w-8 h-8 text-violet-400" />
            </div>
            
            <h1 className="font-heading font-bold text-4xl sm:text-5xl uppercase tracking-wide mb-4">
              <span className="gradient-text">Rejoindre le serveur</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Suivez ces étapes simples pour rejoindre {settings.server_name} sur FiveM.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-xl p-6 relative overflow-hidden group hover:border-violet-500/30 transition-all"
              >
                {/* Step Number Background */}
                <div className="absolute -right-4 -bottom-4 font-heading font-bold text-[120px] text-white/[0.02] select-none">
                  {step.number}
                </div>
                
                <div className="relative flex items-start gap-6">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-violet-600/20 flex items-center justify-center group-hover:bg-violet-600/30 transition-colors">
                      <step.icon className="w-7 h-7 text-violet-400" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-7 h-7 rounded-full bg-violet-600 flex items-center justify-center text-white font-heading font-bold text-sm">
                        {step.number}
                      </span>
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 leading-relaxed mb-4">
                      {step.description}
                    </p>
                    
                    {step.action && (
                      <a
                        href={step.action.url}
                        target={step.action.external ? '_blank' : undefined}
                        rel={step.action.external ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 font-medium transition-colors"
                        data-testid={`step-${step.number}-action`}
                      >
                        {step.action.label}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Connect */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-xl p-8 text-center relative overflow-hidden"
          >
            {/* Background Glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
            
            <div className="relative">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
              
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-wider mb-2">
                Connexion Rapide
              </h2>
              <p className="text-gray-400 mb-6">
                Utilisez ce lien pour vous connecter directement au serveur
              </p>
              
              {/* Server Address */}
              <div className="inline-flex items-center gap-3 glass px-6 py-4 rounded-xl mb-6">
                <Server className="w-5 h-5 text-violet-400" />
                <code className="text-lg text-white font-mono">{settings.server_ip}</code>
              </div>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href={`https://${settings.server_ip}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-heading font-bold uppercase tracking-wider px-8 py-4 btn-skew transition-all duration-300 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]"
                  data-testid="quick-connect-btn"
                >
                  <span className="flex items-center gap-2">
                    <Gamepad2 className="w-5 h-5" />
                    Rejoindre maintenant
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Discord Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-xl p-8 border border-[#5865F2]/30 bg-[#5865F2]/5"
          >
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-xl bg-[#5865F2]/20 flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-[#5865F2]" />
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider mb-2">
                  Besoin d'aide ?
                </h3>
                <p className="text-gray-400">
                  Rejoignez notre Discord pour poser vos questions à la communauté et au staff.
                </p>
              </div>
              
              <a
                href={settings.discord_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752C4] text-white font-heading font-bold uppercase tracking-wider px-6 py-3 rounded-lg transition-all"
                data-testid="discord-help-btn"
              >
                Rejoindre Discord
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Join;
